import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '@/components/SectionHeading';
import { useInView } from '@/lib/hooks';
import { PROCESS } from '@/constants/testIds';

const STEPS = [
  { n: '01', title: 'Discovery Call',       copy: 'You talk. We listen. Smart questions about your business, goals, and the clients you want to attract.' },
  { n: '02', title: 'Proposal in 24 Hours', copy: 'A custom plan with design direction, timeline, and transparent pricing. No surprises.' },
  { n: '03', title: 'Build + Revise',       copy: 'We build fast and share previews early. Most clients need just 1–2 rounds of feedback.' },
  { n: '04', title: 'Launch & Hand Off',    copy: 'We deploy, set up hosting, and hand you the keys. We don’t disappear after launch.' },
];

function StepBadge({ n, inView, delay }) {
  return (
    <div className="relative mx-auto w-[90px] h-[90px]" aria-hidden>
      <svg width="90" height="90" viewBox="0 0 90 90">
        {/* Track hex */}
        <polygon
          points="45,6 80,26 80,64 45,84 10,64 10,26"
          fill="#FFFFFF"
          stroke="#EFEDE6"
          strokeWidth="1"
        />
        {/* Drawn amber stroke */}
        <motion.polygon
          points="45,6 80,26 80,64 45,84 10,64 10,26"
          fill="none"
          stroke="#E89A1F"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeDasharray="260"
          initial={{ strokeDashoffset: 260 }}
          animate={inView ? { strokeDashoffset: 0 } : {}}
          transition={{ duration: 1.1, delay }}
        />
        <text x="45" y="52" textAnchor="middle" fontFamily="Syne, sans-serif" fontSize="21" fontWeight="600" fill="#0F1A33">
          {n}
        </text>
      </svg>
    </div>
  );
}

export default function Process() {
  const [ref, inView] = useInView({ threshold: 0.2, once: true });
  return (
    <section id="process" className="section bg-[color:var(--sh-surface-2)]/40" data-testid={PROCESS.block}>
      <div className="container-pro">
        <SectionHeading
          eyebrow="THE PROCESS"
          title="From 'I need a website' to 'we’re live' — in days."
          subtitle="Four steps. Zero confusion. Here’s exactly how we work."
        />
        <div ref={ref} className="relative grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
          <motion.div
            className="process-line"
            initial={{ scaleX: 0, transformOrigin: 'left center' }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 1.5, delay: 0.2 }}
          />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={PROCESS.step(i)}
              className="text-center px-2 relative"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
            >
              <StepBadge n={s.n} inView={inView} delay={0.2 + i * 0.1} />
              <h4 className="font-display text-lg md:text-xl font-semibold mt-5 text-[color:var(--sh-ink)]">{s.title}</h4>
              <p className="mt-2 text-[14.5px] text-[color:var(--sh-muted)] leading-relaxed max-w-[260px] mx-auto">{s.copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
